"use client";
import { useMemo } from "react";
import {
  CSCDrawerHeader,
  type DrawerHeaderProps,
  AtButton,
  AtButtonType,
  AtTagFolioStatus,
} from "@csc-dashboard/components-library";
import { ScopedModal, ScopedDrawer } from "./components";
import "@csc-dashboard/admin-panel/styles";

import { useAdminPanel } from "./context/admin-panel";
import {
  BottomButton,
  ClientContainer,
  HandleButton,
  TopContent,
  FoliosContainer,
  FolioDetailContainer,
  NoteForm,
  NotesComponent,
} from "./components/";

const messages = {
  guest: {
    title: "¿Estás seguro que deseas salir?",
    description: "Si sales ahora, la bolsa de este cliente anónimo se perderá.",
  },
  registered: {
    title: "¿Finalizar atención?",
    description: "Finaliza la atención si has terminado de atender al cliente.",
  },
};

export const AdminPanel = () => {
  const {
    agentName,
    closeAttention,
    togglePanel,
    isOpen,
    isModalOpen,
    toggleModal,
    isLoading,
    isGuest,
    isForbidden,
  } = useAdminPanel();

  // Memoized header data to prevent recreation
  const headerDarkData: DrawerHeaderProps = useMemo(
    () => ({
      title: "Agente",
      subtitle: agentName || "Cargando...",
      icon: "support-agent-filled",
      variant: "dark",
      actionButton: {
        "data-testid": "drawer-header-button",
        text: "Finalizar atención",
        onClick: toggleModal,
      },
    }),
    [agentName, toggleModal],
  );

  return (
    <>
      <HandleButton onClick={togglePanel} disabled={isForbidden} />

      <ScopedDrawer
        data-testid="drawer"
        isOpen={isOpen}
        handleClose={isLoading ? () => {} : togglePanel}
      >
        <ScopedDrawer.Header>
          <CSCDrawerHeader {...headerDarkData} />
        </ScopedDrawer.Header>
        <RenderTabContent />
      </ScopedDrawer>

      <ScopedModal
        open={isModalOpen}
        onClose={isLoading ? () => {} : toggleModal}
      >
        <div className="flex flex-col gap-10 text-center text-surface-500">
          <div className="flex flex-col gap-6">
            <h2 className="text-2xl">
              {messages[isGuest ? "guest" : "registered"].title}
            </h2>
            <p className="text-lg">
              {messages[isGuest ? "guest" : "registered"].description}
            </p>
          </div>
          <div className="flex gap-4 w-full">
            <AtButton
              disabled={isLoading}
              buttonType={AtButtonType.SECONDARY}
              text="Cancelar"
              onClick={toggleModal}
              className="flex-1"
            />
            <AtButton
              text="Finalizar"
              buttonType={AtButtonType.PRIMARY}
              onClick={closeAttention}
              className="flex-1"
              loading={isLoading}
            />
          </div>
        </div>
      </ScopedModal>
    </>
  );
};

const RenderTabContent = () => {
  const {
    hideAlert,
    removeAlert,
    currentTab = "client",
    setCurrentTab,
    folioId,
    folioStatus,
  } = useAdminPanel();

  switch (currentTab) {
    case "folio-detail":
      return <FolioDetailContainer />;
    case "notes-form":
      return <NoteForm />;
    default:
      return (
        <>
          <ScopedDrawer.Content>
            <div className="p-2 h-full flex flex-col">
              <TopContent
                hideAlert={hideAlert}
                folio={folioId}
                removeAlert={removeAlert}
                selectedTab={currentTab}
                handleTabChange={setCurrentTab}
                folioStatus={folioStatus as AtTagFolioStatus}
              />

              <TabContent />
            </div>
          </ScopedDrawer.Content>
          <ScopedDrawer.Footer>
            <BottomButton />
          </ScopedDrawer.Footer>
        </>
      );
  }
};

const TabContent = () => {
  const { currentTab } = useAdminPanel();

  switch (currentTab) {
    case "client":
      return <ClientContainer />;
    case "folios":
      return <FoliosContainer />;
    case "notes":
      return <NotesComponent />;
  }
};
